import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Trophy, Heart, Lightning, ShieldCheck, Leaf, Quotes,
  Briefcase, Users, Rocket, Check
} from "@phosphor-icons/react";
import { COMPANY, IMAGES } from "@/data/site";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

const VALUES = [
  { icon: ShieldCheck, t: "Trust", d: "Transparent pricing, honest timelines." },
  { icon: Lightning, t: "Innovation", d: "AI, IoT and renewable-first thinking." },
  { icon: Trophy, t: "Quality", d: "Tier-1 components, 32-point QC." },
  { icon: Heart, t: "Customer First", d: "Direct founder access, 24-hr SLA." },
  { icon: Leaf, t: "Sustainability", d: "Every install offsets 1.2t CO₂/kW/yr." },
];

const JOURNEY = [
  {
    year: "2013", brand: "Fine Green Solar",
    desc: "Our journey began with a mission to promote clean and sustainable energy through high-quality Solar Power Solutions for homes, businesses and agriculture.",
    label: "Focus", items: ["Solar Power Systems", "Renewable Energy Solutions"], color: "bg-amber-400",
  },
  {
    year: "2017", brand: "Fine Green Traders",
    desc: "To better serve our customers, we expanded into Power Backup Solutions — reliable UPS systems, inverters and batteries alongside solar.",
    label: "Expanded Services", items: ["Solar Power Systems", "UPS Systems", "Inverters", "Batteries"], color: "bg-blue-500",
  },
  {
    year: "2020", brand: "Fine Green Technology",
    desc: "As technology advanced, we introduced Security & Surveillance Solutions, helping customers protect homes and businesses with smart CCTV systems.",
    label: "New Services Added", items: ["CCTV Surveillance", "Security Systems", "Networking Solutions"], color: "bg-slate-900",
  },
  {
    year: "2022", brand: "Tamil BTC",
    desc: "A new brand with a broader vision. Tamil BTC expanded into Digital Technology and Crypto Education, building a technology-driven community.",
    label: "New Services Added", items: ["Crypto Education & Community", "Website Development", "Digital & IT Services"], color: "bg-violet-500",
  },
  {
    year: "2024", brand: "TamilBTC Traders",
    desc: "We strengthened our portfolio with Smart Tracking and IoT Solutions to meet growing demand for connected technologies.",
    label: "New Services Added", items: ["GPS Vehicle Tracking", "Smart Sensors", "IoT-Based Monitoring"], color: "bg-emerald-500",
  },
  {
    year: "2026", brand: "TamilBTC Technologies", current: true,
    desc: "Today, TamilBTC Technologies is a complete Digital Energy & Security Company — end-to-end solutions for residential, commercial, industrial and institutional customers.",
    label: "Current Services", items: ["Solar Power Systems", "CCTV Surveillance", "Electric Scooters", "Lithium Battery Solutions", "UPS & Inverters", "ELV Systems", "Networking", "Website Development", "AI & Digital Solutions", "GPS & Smart Sensors"], color: "bg-amber-400",
  },
];

const UPCOMING = [
  "Home Interior Design", "False Ceiling Solutions", "Gate Automation",
  "Home Automation", "Smart Home Solutions", "EV Charging Infrastructure", "Smart Energy Management Systems",
];

const CEO_TIMELINE = [
  { year: "2012", title: "Graduated B.E. — Electrical & Electronics Engineering", sub: "Periyar Maniammai University" },
  { year: "2016", title: "Apprentice Engineer", sub: "KGTPS — Tamil Nadu Electricity Board (TNEB)" },
  { year: "2018", title: "Certified Blockchain Enthusiast", sub: "" },
  { year: "2019", title: "Community Ambassador — CoinEx", sub: "" },
  { year: "2020", title: "Certified Solar Grid Engineer", sub: "Key Opinion Leader (Bitget) • Brand Ambassador — BEAM" },
  { year: "2021", title: "Key Opinion Leader (KOL)", sub: "BingX • BitMart" },
  { year: "2022", title: "Key Opinion Leader (KOL)", sub: "MEXC Global" },
];

const CEO_ROLES = [
  "Founder & CEO – TamilBTC Technologies",
  "Founder – Namma Crypto",
  "Founder – TamilBTC YouTube",
  "Founder – 5o.wiki",
  "District President – Aam Aadmi Party, Thiruvarur",
  "Member – YES (Young Entrepreneurs Society)",
  "Rotarian (Rotary Member)",
  "Member – AIETA (All India Electricians & Technicians Association)",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">About</div>
          <h1 className="mt-4 font-heading font-black text-5xl md:text-7xl tracking-tighter text-slate-950 max-w-4xl">
            Empowering Tamil Nadu through <span className="italic font-light">Energy, Security &amp; Digital Innovation.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-slate-600 leading-relaxed">From a single solar business in 2013 to Tamil Nadu's most trusted multi-division technology partner — this is our journey.</p>
        </div>
      </section>

      {/* OUR JOURNEY TIMELINE */}
      <section className="bg-slate-50 py-20 lg:py-28" data-testid="journey-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Our Journey</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-slate-950 max-w-3xl">From Renewable Energy to <span className="italic font-light">Smart Technology.</span></h2>
          <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">Our story is one of continuous innovation and growth. Since 2013, we have evolved from a solar-focused business into a complete Digital Energy, Security, Mobility and Technology solutions provider.</p>

          <div className="mt-16 relative">
            <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-px bg-slate-300 md:-translate-x-1/2" />
            <div className="space-y-10">
              {JOURNEY.map((j, i) => (
                <Reveal key={j.year} delay={i * 0.04}>
                  <div className={`relative flex flex-col md:flex-row gap-6 md:gap-12 ${i % 2 === 1 ? "md:flex-row-reverse" : ""}`}>
                    <div className={`absolute left-[15px] md:left-1/2 w-4 h-4 ${j.color} md:-translate-x-1/2 mt-1.5 ring-4 ring-slate-50`} />
                    <div className="hidden md:block md:w-1/2" />
                    <div className="pl-10 md:pl-0 md:w-1/2">
                      <div className={`card-flat p-7 ${j.current ? "ring-2 ring-amber-400" : ""}`} data-testid={`journey-${j.year}`}>
                        <div className="flex items-center gap-3">
                          <span className={`inline-block ${j.color} ${j.color === "bg-amber-400" ? "text-slate-950" : "text-white"} font-heading font-black text-lg px-3 py-1`}>{j.year}</span>
                          {j.current && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-950 text-white px-2 py-1">Today</span>}
                        </div>
                        <h3 className="mt-4 font-heading text-2xl font-bold text-slate-950">{j.brand}</h3>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{j.desc}</p>
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{j.label}</div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {j.items.map((it) => (
                              <span key={it} className="text-xs font-medium border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">{it}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* LOOKING AHEAD */}
      <section className="bg-slate-950 text-white py-20 lg:py-28" data-testid="looking-ahead-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-eyebrow text-amber-400">Looking Ahead</div>
            <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter">Our journey doesn't stop here.</h2>
            <p className="mt-5 text-slate-300 leading-relaxed">We are expanding into complete smart living and infrastructure solutions to better serve the future needs of our customers.</p>
            <Rocket size={40} weight="duotone" className="mt-8 text-amber-400" />
          </div>
          <div className="md:col-span-7">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-4">Upcoming Divisions</div>
            <div className="grid sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800">
              {UPCOMING.map((u, i) => (
                <div key={u} className="bg-slate-950 p-5 flex items-center gap-3" data-testid={`upcoming-${i}`}>
                  <Check size={16} weight="bold" className="text-amber-400 shrink-0" />
                  <span className="text-sm font-medium text-slate-200">{u}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
          <div className="bg-white p-12">
            <div className="label-eyebrow text-amber-600">Mission</div>
            <h3 className="mt-3 font-heading text-3xl md:text-4xl font-bold tracking-tight text-slate-950 leading-tight">Provide affordable and reliable technology infrastructure across Tamil Nadu.</h3>
          </div>
          <div className="bg-slate-950 text-white p-12">
            <div className="label-eyebrow text-amber-400">Our Vision</div>
            <h3 className="mt-3 font-heading text-2xl md:text-3xl font-bold tracking-tight leading-snug">To become Tamil Nadu's most trusted Digital Energy, Security, Mobility &amp; Smart Technology Company — delivering innovative, sustainable, customer-focused solutions under one brand.</h3>
            <p className="mt-6 font-heading text-amber-400 font-semibold">Powering Your World. Intelligently.</p>
            <p className="mt-2 text-xs text-slate-400">Smart Energy • Smart Security • Smart Mobility • Smart Living • Smart Technology</p>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-slate-50 py-20" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="label-eyebrow">Core Values</div>
          <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950 max-w-2xl">Five values. Every decision.</h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-white p-8" data-testid={`value-${v.t.toLowerCase().replace(' ', '-')}`}>
                <v.icon size={32} weight="duotone" className="text-amber-500" />
                <h4 className="mt-5 font-heading font-bold text-xl text-slate-950">{v.t}</h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEET OUR CEO */}
      <section className="bg-white py-20 lg:py-28" data-testid="ceo-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <div className="card-flat overflow-hidden sticky top-24">
                <img src={IMAGES.founder} alt="CEO" className="w-full h-[520px] object-cover" />
                <div className="p-6">
                  <div className="font-heading font-bold text-xl text-slate-950">{COMPANY.founder}, B.E.</div>
                  <div className="text-sm text-slate-500 mt-1">Founder &amp; CEO — TamilBTC Technologies</div>
                </div>
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="label-eyebrow">Meet Our CEO</div>
              <h2 className="mt-3 font-heading font-bold text-4xl md:text-5xl tracking-tighter text-slate-950">{COMPANY.founder}</h2>
              <p className="mt-2 text-slate-500 font-medium">B.E. (Electrical &amp; Electronics Engineering) • Founder &amp; CEO</p>
              <p className="mt-6 text-slate-700 leading-relaxed">An entrepreneur, technology enthusiast, renewable energy professional and community leader with over a decade of experience across electrical engineering, solar energy, digital technologies and blockchain.</p>
              <p className="mt-4 text-slate-700 leading-relaxed">He completed his B.E. in Electrical &amp; Electronics Engineering from Periyar Maniammai University in 2012, and has since continuously expanded his expertise across renewable energy, emerging technologies, digital innovation and entrepreneurship.</p>

              {/* Professional Journey timeline */}
              <div className="mt-10">
                <div className="flex items-center gap-2 label-eyebrow"><Briefcase size={14} weight="bold" /> Professional Journey</div>
                <div className="mt-5 relative pl-8">
                  <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200" />
                  <div className="space-y-6">
                    {CEO_TIMELINE.map((c, i) => (
                      <div key={i} className="relative" data-testid={`ceo-timeline-${c.year}`}>
                        <div className="absolute -left-8 w-4 h-4 bg-amber-400 ring-4 ring-white mt-0.5" />
                        <div className="font-heading font-black text-slate-950">{c.year}</div>
                        <div className="text-sm font-semibold text-slate-800 mt-0.5">{c.title}</div>
                        {c.sub && <div className="text-xs text-slate-500 mt-0.5">{c.sub}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Founder of */}
              <div className="mt-10">
                <div className="flex items-center gap-2 label-eyebrow"><Rocket size={14} weight="bold" /> Founder Of</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["TamilBTC Technologies", "Namma Crypto", "TamilBTC YouTube Channel", "5o.wiki"].map((f) => (
                    <span key={f} className="text-xs font-semibold bg-slate-950 text-white px-3 py-1.5">{f}</span>
                  ))}
                </div>
              </div>

              {/* Leadership & community */}
              <div className="mt-10">
                <div className="flex items-center gap-2 label-eyebrow"><Users size={14} weight="bold" /> Leadership &amp; Community</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">Beyond business, Vinothkumar actively contributes to entrepreneurship, professional networking and public service.</p>
                <div className="mt-4 grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
                  {CEO_ROLES.map((r, i) => (
                    <div key={i} className="bg-white p-4 flex items-start gap-2.5" data-testid={`ceo-role-${i}`}>
                      <Check size={15} weight="bold" className="text-emerald-500 shrink-0 mt-0.5" />
                      <span className="text-xs font-medium text-slate-700">{r}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vision quote */}
              <div className="mt-10 bg-slate-950 text-white p-8">
                <Quotes size={32} weight="fill" className="text-amber-400" />
                <p className="mt-4 font-heading text-xl md:text-2xl leading-snug">"Technology should make life smarter, cleaner, safer and more affordable. My vision is to build TamilBTC Technologies into one of Tamil Nadu's most trusted companies for renewable energy, security, electric mobility and digital innovation — while creating lasting value for customers and society."</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-1 h-10 bg-amber-400" />
                  <div>
                    <div className="font-heading font-bold">{COMPANY.founder}, B.E.</div>
                    <div className="text-xs text-slate-400">Founder &amp; CEO, TamilBTC Technologies</div>
                  </div>
                </div>
              </div>

              <Link to="/contact" data-testid="about-cta" className="mt-10 inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3.5 text-sm font-semibold hover:bg-slate-800">
                Work with us <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
